import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';

const RichSelectField = ({
  label,
  id,
  value = '',
  onChange,
  onBlur,
  options = [],
  placeholder = 'Select option',
  required = false,
  disabled = false,
  className = '',
  containerClassName = '',
  error,
  useCustomDropdown = true,
  maxHeight = '200px',
  formatValue, // Function to format option objects for display
  valueKey = 'value', // Key to use for value comparison when using objects
  ...props
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  // Custom dropdown state
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [inputValue, setInputValue] = useState('');
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  // Helper function to normalize options to consistent format
  const normalizeOption = (option) => {
    if (typeof option === 'string') {
      return { value: option, label: option };
    }
    
    // If formatValue is provided and option is an object, use it
    if (formatValue && typeof option === 'object' && !option?.label?.length > 0) {
      const formatted = formatValue(option);
      return {
        value: option[valueKey] || option.value,
        label: formatted.label || formatted,
        subtitle: formatted.subtitle,
        description: formatted.description,
        icon: formatted.icon,
        ...formatted
      };
    }
    
    return option;
  };

  // Normalize all options
  const normalizedOptions = options.map(normalizeOption);

  // Determine if autocomplete should be shown
  const showAutocomplete = normalizedOptions.length > 10;

  // Filter options based on input value (only when user is actively typing)
  const filteredOptions = showAutocomplete && inputValue && isOpen
    ? normalizedOptions.filter(option => {
        const searchLower = inputValue.toLowerCase();
        return (
          option.label?.toLowerCase().includes(searchLower) ||
          option.subtitle?.toLowerCase().includes(searchLower) ||
          option.description?.toLowerCase().includes(searchLower)
        );
      })
    : normalizedOptions;

  // Get selected option for display
  const selectedOption = normalizedOptions.find(opt => opt.value === value);

  // Handle blur events
  const handleBlur = (event) => {
    if (onBlur) {
      onBlur(event);
    }
  };

  // Sync input value with selected option
  useEffect(() => {
    if (!showAutocomplete) return;
    
    if (selectedOption && !isOpen) {
      setInputValue(selectedOption.label);
    } else if (!isOpen) {
      setInputValue('');
    }
  }, [selectedOption, isOpen, showAutocomplete]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!useCustomDropdown) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
        // Reset input to selected option label
        if (showAutocomplete && selectedOption) {
          setInputValue(selectedOption.label);
        } else if (showAutocomplete) {
          setInputValue('');
        }
        // Trigger blur when dropdown closes due to outside click
        if (onBlur) {
          const syntheticEvent = {
            target: { value, id },
            currentTarget: { value, id }
          };
          onBlur(syntheticEvent);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [useCustomDropdown, onBlur, value, id, showAutocomplete, selectedOption]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!useCustomDropdown || !isOpen) return;

    const handleKeyDown = (event) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev < filteredOptions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : filteredOptions.length - 1
          );
          break;
        case 'Enter':
          event.preventDefault();
          if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            handleOptionSelect(filteredOptions[highlightedIndex]);
          }
          break;
        case 'Escape':
          setIsOpen(false);
          setHighlightedIndex(-1);
          if (showAutocomplete && selectedOption) {
            setInputValue(selectedOption.label);
          } else if (showAutocomplete) {
            setInputValue('');
          }
          triggerRef.current?.blur();
          break;
        case 'Tab':
          setIsOpen(false);
          setHighlightedIndex(-1);
          if (showAutocomplete && selectedOption) {
            setInputValue(selectedOption.label);
          } else if (showAutocomplete) {
            setInputValue('');
          }
          // Trigger blur when tabbing away
          if (onBlur) {
            const syntheticEvent = {
              target: { value, id },
              currentTarget: { value, id }
            };
            onBlur(syntheticEvent);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    useCustomDropdown,
    isOpen,
    highlightedIndex,
    filteredOptions,
    onBlur,
    value,
    id,
    showAutocomplete,
    selectedOption
  ]);

  const handleOptionSelect = (option) => {
    const event = {
      target: { value: option.value }
    };
    onChange(event);
    setIsOpen(false);
    setHighlightedIndex(-1);
    if (showAutocomplete) {
      setInputValue(option.label);
    }
  };

  const handleTriggerClick = () => {
    if (disabled) return;
    if (!isOpen) {
      setIsOpen(true);
      setHighlightedIndex(-1);
      // For autocomplete, clear the input when opening to show all options
      if (showAutocomplete) {
        setInputValue('');
        setTimeout(() => triggerRef.current?.focus(), 0);
      }
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
    setHighlightedIndex(-1);
  };

  const labelClassName = clsx(
    'block text-sm font-normal mb-2',
    {
      'text-slate-200': isDark,
      'text-gray-700': !isDark,
      'text-red-400': error && isDark,
      'text-red-600': error && !isDark
    }
  );

  const selectClassName = clsx(
    'w-full text-sm px-3 py-3 border rounded-lg transition-colors appearance-none',
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
    {
      'bg-slate-800 border-slate-600 text-slate-200': isDark && !error,
      'bg-white border-gray-300 text-gray-900': !isDark && !error,
      'border-red-500 bg-red-50': error && !isDark,
      'border-red-400 bg-slate-800': error && isDark,
      'opacity-50 cursor-not-allowed': disabled
    },
    className
  );

  // Custom dropdown trigger styling
  const triggerClassName = clsx(
    'w-full text-sm px-3 py-3 border rounded-lg transition-colors cursor-pointer',
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
    'flex items-center justify-between',
    {
      'bg-slate-800 border-slate-600 text-slate-200': isDark && !error,
      'bg-white border-gray-300 text-gray-900': !isDark && !error,
      'border-red-500 bg-red-50': error && !isDark,
      'border-red-400 bg-slate-800': error && isDark,
      'opacity-50 cursor-not-allowed': disabled,
      'border-blue-500 ring-2 ring-blue-500': isOpen && !error
    },
    className
  );

  // Input styling for autocomplete mode (no flex needed)
  const inputClassName = clsx(
    'w-full text-sm px-3 py-3 border rounded-lg transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
    {
      'bg-slate-800 border-slate-600 text-slate-200 placeholder-slate-400': isDark && !error,
      'bg-white border-gray-300 text-gray-900 placeholder-gray-400': !isDark && !error,
      'border-red-500 bg-red-50 placeholder-red-300': error && !isDark,
      'border-red-400 bg-slate-800 placeholder-red-300': error && isDark,
      'opacity-50 cursor-not-allowed': disabled,
      'border-blue-500 ring-2 ring-blue-500': isOpen && !error
    },
    className
  );

  const dropdownClassName = clsx(
    'absolute top-full left-0 right-0 z-[9999] mt-1 border rounded-lg shadow-lg overflow-hidden',
    {
      'bg-slate-800 border-slate-600': isDark,
      'bg-white border-gray-300': !isDark
    }
  );

  const optionClassName = (isHighlighted, isSelected) => clsx(
    'px-3 py-2 cursor-pointer transition-colors',
    {
      // Dark theme
      'bg-slate-700 text-white': isDark && (isHighlighted || isSelected),
      'text-slate-200 hover:bg-slate-700': isDark && !isHighlighted && !isSelected,
      // Light theme  
      'bg-blue-50 text-blue-900': !isDark && (isHighlighted || isSelected),
      'text-gray-900 hover:bg-gray-50': !isDark && !isHighlighted && !isSelected
    }
  );

  const containerClasses = clsx('relative', containerClassName);

  const iconClassName = clsx(
    'transition-transform duration-200',
    {
      'text-slate-400': isDark,
      'text-gray-400': !isDark,
      'transform rotate-180': isOpen && useCustomDropdown
    }
  );

  const errorClassName = clsx(
    'text-sm mt-1',
    {
      'text-red-400': isDark,
      'text-red-600': !isDark
    }
  );

  // Render option content (supports icons and multi-line)
  const renderOptionContent = (option) => {
    if (option.icon || option.subtitle || option.description) {
      return (
        <div className="flex items-start gap-3">
          {option.icon && (
            <div className="flex-shrink-0 mt-0.5">
              {option.icon}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{option.label}</div>
            {option.subtitle && (
              <div className={clsx('text-xs', {
                'text-slate-400': isDark,
                'text-gray-500': !isDark
              })}>
                {option.subtitle}
              </div>
            )}
            {option.description && (
              <div className={clsx('text-xs mt-1', {
                'text-slate-500': isDark,
                'text-gray-600': !isDark
              })}>
                {option.description}
              </div>
            )}
          </div>
        </div>
      );
    }
    return option.label;
  };

  // Render trigger content
  const renderTriggerContent = () => {
    if (!selectedOption) {
      return (
        <span className={clsx({
          'text-slate-400': isDark,
          'text-gray-400': !isDark
        })}>
          {placeholder}
        </span>
      );
    }

    if (selectedOption.icon || selectedOption.subtitle) {
      return (
        <div className="flex items-center gap-2 min-w-0">
          {selectedOption.icon && (
            <div className="flex-shrink-0">
              {selectedOption.icon}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="truncate">{selectedOption.label}</div>
            {selectedOption.subtitle && (
              <div className={clsx('text-xs truncate', {
                'text-slate-400': isDark,
                'text-gray-500': !isDark
              })}>
                {selectedOption.subtitle}
              </div>
            )}
          </div>
        </div>
      );
    }

    return selectedOption.label;
  };

  if (useCustomDropdown) {
    return (
      <div className={containerClassName}>
        {label && (
          <label htmlFor={id} className={labelClassName}>
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className={containerClasses} ref={dropdownRef}>
          {showAutocomplete ? (
            // Autocomplete mode: render as input field
            <input
              ref={triggerRef}
              type="text"
              className={inputClassName}
              value={inputValue}
              onChange={handleInputChange}
              onClick={handleTriggerClick}
              onFocus={() => {
                if (!isOpen) {
                  setIsOpen(true);
                  setInputValue('');
                }
              }}
              onBlur={handleBlur}
              placeholder={placeholder}
              disabled={disabled}
              autoComplete="off"
              role="combobox"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
              aria-autocomplete="list"
            />
          ) : (
            // Regular mode: render as clickable div
            <div
              ref={triggerRef}
              className={triggerClassName}
              onClick={handleTriggerClick}
              onBlur={handleBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTriggerClick();
                }
              }}
              tabIndex={disabled ? -1 : 0}
              role="combobox"
              aria-expanded={isOpen}
              aria-haspopup="listbox"
            >
              <div className="flex-1 min-w-0">
                {renderTriggerContent()}
              </div>
              <ChevronDown size={16} className={iconClassName} />
            </div>
          )}
          
          {isOpen && (
            <div className={dropdownClassName} style={{ maxHeight, overflowY: 'auto' }}>
              {filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  className={optionClassName(
                    index === highlightedIndex,
                    option.value === value
                  )}
                  onClick={() => handleOptionSelect(option)}
                  role="option"
                  aria-selected={option.value === value}
                >
                  {renderOptionContent(option)}
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className={clsx('px-3 py-2 text-sm', {
                  'text-slate-400': isDark,
                  'text-gray-500': !isDark
                })}>
                  {inputValue ? 'No matches found' : 'No options available'}
                </div>
              )}
            </div>
          )}
        </div>
        {error && (
          <div className={errorClassName}>
            {error}
          </div>
        )}
      </div>
    );
  }

  // Original browser select fallback
  return (
    <div className={containerClassName}>
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className={containerClasses}>
        <select
          id={id}
          data-testid={id}
          value={value}
          onChange={onChange}
          onBlur={handleBlur}
          disabled={disabled}
          className={selectClassName}
          {...props}
        >
          <option value="">{placeholder}</option>
          {normalizedOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={16}
          className={clsx(iconClassName, 'absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none')} />
      </div>
      {error && (
        <div className={errorClassName}>
          {error}
        </div>
      )}
    </div>
  );
};

RichSelectField.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string, // Simple string options
      PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        subtitle: PropTypes.string, // New: secondary text
        description: PropTypes.string, // New: additional description
        icon: PropTypes.node // New: icon component
      }),
      PropTypes.object // Any object when using formatValue
    ])
  ),
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  error: PropTypes.string,
  useCustomDropdown: PropTypes.bool, // New: enables custom dropdown
  maxHeight: PropTypes.string, // New: custom dropdown max height
  formatValue: PropTypes.func, // New: function to format option objects
  valueKey: PropTypes.string // New: key to use for value comparison
};

export default RichSelectField;